import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, TreePine, Waves, Flame, Route, Camera } from "lucide-react";
import { ESTATE_IMG, TRAIL_IMG, MAP_LINK } from "./data";
import SectionHeader from "./SectionHeader";

const items = [
  { icon: Camera, title: "Hashtag View Point", note: "One of Sakleshpura's most-shot sunrise decks — 240 m from your door." },
  { icon: Route, title: "Trekking Trails", note: "From the estate to Hosahalli Gudda and Hosahalli Betta." },
  { icon: Waves, title: "Natural Pond", note: "Fed by monsoon streams, framed by silver oaks." },
  { icon: TreePine, title: "Off-road Rides", note: "Estate roads carved for the 4×4, the Enfield, and the brave scooter." },
  { icon: Flame, title: "Private Camping", note: "A quiet clearing set aside for stargazing and long conversations." },
  { icon: MapPin, title: "25-acre Coffee Estate", note: "Robusta rows, jackfruit shade trees, pepper vines climbing everywhere." },
];

const reveal = {
  hidden: { y: 30, opacity: 0 },
  show: (i) => ({
    y: 0,
    opacity: 1,
    transition: { delay: i * 0.05, duration: 0.8, ease: [0.2, 0.9, 0.2, 1] },
  }),
};

export default function About() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y1 = useTransform(scrollYProgress, [0, 1], ["-6%", "8%"]);
  const y2 = useTransform(scrollYProgress, [0, 1], ["6%", "-6%"]);

  return (
    <section
      id="about"
      ref={ref}
      data-testid="about-section"
      className="relative bg-[color:var(--paper)] text-[color:var(--ink)]"
    >
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 py-16 sm:py-20 lg:py-24">
        <SectionHeader
          eyebrow="Section 01 · About Us"
          title={
            <>
              A homestay <span className="font-display-italic text-[color:var(--clay)]">tucked</span> inside
              twenty-five acres of coffee, at the edge of the Western Ghats.
            </>
          }
          intro="Come slow. Wake to fog, walk into the coffee, sit by the fire at night."
        />

        {/* Experience highlight */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease: [0.2, 0.9, 0.2, 1] }}
          data-testid="about-experience-badge"
          className="mt-10 sm:mt-12 inline-flex items-baseline gap-4 rounded-full border hairline bg-[color:var(--paper-2)] pl-3 pr-6 py-3"
        >
          <span className="inline-flex items-baseline gap-1.5 rounded-full bg-[color:var(--forest)] text-[color:var(--paper)] px-4 py-2">
            <span className="font-display text-[1.55rem] leading-none">6.5</span>
            <span className="font-mono-alt text-[color:var(--sand)]">yrs</span>
          </span>
          <span className="font-display text-[1.05rem] sm:text-[1.2rem] leading-tight">
            of hosting guests in the Sakleshpura hills
          </span>
        </motion.div>

        {/* Layout swap per feedback:
            TEXT on the LEFT, IMAGES on the RIGHT. */}
        <div className="mt-12 sm:mt-16 grid grid-cols-12 gap-6 lg:gap-10 items-start">
          {/* Text column */}
          <div className="col-span-12 lg:col-span-5 lg:pt-4 order-2 lg:order-1">
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9 }}
              className="font-display text-[1.25rem] sm:text-[1.45rem] leading-[1.35]"
            >
              We live inside our coffee. From the veranda you can see rows of
              robusta disappear into fog by 5 p.m. and reappear by 8 a.m. the
              next morning. In the middle of it — a home for you.
            </motion.p>

            <p className="mt-5 text-[color:var(--ink)]/75 max-w-md">
              A short walk brings you to <b className="text-[color:var(--forest)]">Hashtag View Point</b>,
              one of Sakleshpura's finest shooting spots. A little further,
              the trails to Hosahalli Gudda and Abbi Falls. Come for the
              mist, stay for the fire, leave with the smell of estate coffee
              on your jacket.
            </p>

            <a
              href={MAP_LINK}
              data-testid="about-map-link"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-2 font-mono-alt text-[color:var(--forest)] hover-underline"
            >
              <MapPin size={14} /> Open in Google Maps
            </a>
          </div>

          {/* Images duo — RIGHT column */}
          <div className="col-span-12 lg:col-span-7 order-1 lg:order-2 grid grid-cols-2 gap-4 sm:gap-6">
            <motion.div
              style={{ y: y1 }}
              className="relative aspect-[3/4] w-full overflow-hidden rounded-[28px] bg-[color:var(--forest)]"
            >
              <img
                src={ESTATE_IMG}
                alt="Coffee estate cottage in the mist"
                className="h-full w-full object-cover"
                loading="lazy"
              />
            </motion.div>
            <motion.div
              style={{ y: y2 }}
              className="relative aspect-[3/4] w-full overflow-hidden rounded-[28px] bg-[color:var(--forest)] mt-8"
            >
              <img
                src={TRAIL_IMG}
                alt="Misty forest trail inside the estate"
                className="h-full w-full object-cover"
                loading="lazy"
              />
            </motion.div>
          </div>
        </div>

        {/* Feature strip */}
        <div className="mt-14 sm:mt-20 grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-8 border-t hairline pt-8">
          {items.map((it, i) => (
            <motion.div
              key={it.title}
              variants={reveal}
              custom={i}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-40px" }}
              className="flex flex-col gap-2"
            >
              <div className="flex items-center gap-3">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[color:var(--forest)] text-[color:var(--paper)]">
                  <it.icon size={16} />
                </span>
                <span className="font-mono-alt text-[color:var(--forest)]/70">
                  0{i + 1}
                </span>
              </div>
              <h3 className="font-display text-[1.2rem] leading-tight">
                {it.title}
              </h3>
              <p className="text-[0.88rem] text-[color:var(--ink)]/70 max-w-[26ch]">
                {it.note}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
